import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../lib/api.js';
import { startRegistration } from '@simplewebauthn/browser';
import { fmtDateTime } from '../lib/datetime.js';

export default function Profile() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState('profile');
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">My account</h1>
        <p className="text-sm text-slate-600 mt-1">Signed in as {user?.email}</p>
      </header>
      <nav className="flex gap-2 text-sm border-b border-slate-200">
        {[
          ['profile', 'Profile + password'],
          ['mfa', 'Two-factor auth'],
          ['passkeys', 'Passkeys'],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={
              'px-3 py-2 -mb-px border-b-2 ' +
              (tab === k
                ? 'border-cathay-600 text-cathay-700 font-medium'
                : 'border-transparent text-slate-600 hover:text-cathay-700')
            }
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === 'profile' && <ProfilePanel user={user} refresh={refresh} />}
      {tab === 'mfa' && <MfaPanel user={user} refresh={refresh} />}
      {tab === 'passkeys' && <PasskeyPanel />}
    </div>
  );
}

function ProfilePanel({ user, refresh }) {
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(null);
    setOk(null);
    if (next && next !== confirm) {
      setErr('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      const body = { display_name: displayName.trim() };
      if (next) {
        body.current_password = current;
        body.new_password = next;
      }
      await api.auth.updateMe(body);
      setOk('Saved.');
      setCurrent('');
      setNext('');
      setConfirm('');
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded shadow-sm border p-4 space-y-4 max-w-lg">
      <label className="block">
        <span className="text-sm text-slate-700">Display name</span>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <hr />
      <p className="text-sm text-slate-600">Change password (leave blank to skip).</p>
      <label className="block">
        <span className="text-sm text-slate-700">Current password</span>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <label className="block">
        <span className="text-sm text-slate-700">New password (min 10)</span>
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={10}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <label className="block">
        <span className="text-sm text-slate-700">Confirm new password</span>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
        />
      </label>
      {err && <div className="text-sm text-red-600">{err}</div>}
      {ok && <div className="text-sm text-green-700">{ok}</div>}
      <button
        disabled={busy}
        className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
      >
        Save
      </button>
    </form>
  );
}

function MfaPanel({ user, refresh }) {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.mfa.setup();
      setSetupData(r);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const finishSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.mfa.verifySetup(code.trim());
      setBackupCodes(r.backup_codes);
      setCode('');
      setSetupData(null);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.mfa.disable(disableCode.trim());
      setDisableCode('');
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (user?.mfa_enabled && !backupCodes) {
    return (
      <div className="bg-white rounded shadow-sm border p-4 space-y-4 max-w-lg">
        <div className="text-sm text-green-700">
          Two-factor authentication is <strong>enabled</strong>.
        </div>
        <form onSubmit={disable} className="space-y-3">
          <label className="block">
            <span className="text-sm text-slate-700">Enter current 6-digit code (or backup code) to disable</span>
            <input
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2"
              placeholder="123456"
            />
          </label>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <button
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            Disable 2FA
          </button>
        </form>
      </div>
    );
  }

  if (backupCodes) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-4 max-w-lg">
        <h2 className="font-semibold text-amber-900">Save your backup codes</h2>
        <p className="text-sm text-amber-800 mt-1">
          Each code can be used once if you lose your authenticator. Store them somewhere safe — they
          won't be shown again.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
          {backupCodes.map((c) => (
            <div key={c} className="bg-white border border-amber-200 rounded px-2 py-1">{c}</div>
          ))}
        </div>
        <button
          onClick={() => setBackupCodes(null)}
          className="mt-3 bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-1.5 rounded text-sm"
        >
          I've saved them
        </button>
      </div>
    );
  }

  if (setupData) {
    return (
      <form onSubmit={finishSetup} className="bg-white rounded shadow-sm border p-4 space-y-3 max-w-lg">
        <h2 className="font-medium">Scan with your authenticator app</h2>
        <img src={setupData.qr_data_url} alt="MFA QR" className="border rounded" />
        <p className="text-xs text-slate-600">
          Can't scan? Use this secret: <code className="font-mono">{setupData.secret}</code>
        </p>
        <label className="block">
          <span className="text-sm text-slate-700">Enter the 6-digit code shown in the app</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            className="mt-1 w-full border border-slate-300 rounded px-3 py-2 text-center tracking-widest text-lg"
            placeholder="123456"
            required
          />
        </label>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <button
          disabled={busy}
          className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
        >
          Verify + enable
        </button>
      </form>
    );
  }

  return (
    <div className="bg-white rounded shadow-sm border p-4 max-w-lg space-y-3">
      <p className="text-sm text-slate-700">
        Add a one-time code from an authenticator app (Google Authenticator, Authy, 1Password,
        Microsoft Authenticator) as a second factor.
      </p>
      {err && <div className="text-sm text-red-600">{err}</div>}
      <button
        disabled={busy}
        onClick={startSetup}
        className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
      >
        Set up 2FA
      </button>
    </div>
  );
}

function PasskeyPanel() {
  const [passkeys, setPasskeys] = useState([]);
  const [nickname, setNickname] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const refreshList = async () => {
    try {
      const r = await api.passkey.list();
      setPasskeys(r.passkeys);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refreshList(); }, []);

  const register = async () => {
    setBusy(true);
    setErr(null);
    try {
      const options = await api.passkey.registerOptions();
      const att = await startRegistration({ optionsJSON: options });
      await api.passkey.registerVerify({ response: att, nickname: nickname || 'Passkey' });
      setNickname('');
      await refreshList();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    if (!confirm('Delete this passkey?')) return;
    try {
      await api.passkey.delete(id);
      await refreshList();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="bg-white rounded shadow-sm border p-4 max-w-lg space-y-4">
      <div>
        <h2 className="font-medium text-slate-800">Your passkeys</h2>
        <p className="text-xs text-slate-500 mt-1">
          Passkeys let you sign in with Face ID / Touch ID / Windows Hello without a password.
        </p>
      </div>
      <ul className="divide-y divide-slate-200 text-sm">
        {passkeys.map((p) => (
          <li key={p.id} className="py-2 flex items-center justify-between">
            <div>
              <div className="font-medium">{p.nickname || 'Passkey'}</div>
              <div className="text-xs text-slate-500">
                {p.device_type || 'unknown'} ·{' '}
                {p.last_used_at ? `last used ${fmtDateTime(p.last_used_at)}` : 'never used'}
              </div>
            </div>
            <button onClick={() => remove(p.id)} className="text-xs text-red-600 hover:underline">
              Remove
            </button>
          </li>
        ))}
        {passkeys.length === 0 && (
          <li className="py-2 text-slate-500">No passkeys yet.</li>
        )}
      </ul>
      <div className="flex gap-2 items-end pt-2 border-t border-slate-100">
        <label className="flex-1 block">
          <span className="text-xs text-slate-600">Nickname (optional)</span>
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="MacBook Touch ID"
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
        <button
          disabled={busy}
          onClick={register}
          className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
        >
          Add passkey
        </button>
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}
    </div>
  );
}
